"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const useUniqueId_1 = require("../useUniqueId");
/**
 * A low-level hook that implements the WAI-ARIA 1.1 specification for
 * a listbox. This hook only implements a part of the specification,
 * leaving other stateful parts up to the consumer: particularly the
 * parts involving state and multiselect capabilities.
 */
exports.useListbox = ({ itemCount, isMultiSelect = false, isDisabled = false, initiallyOpen = false, }) => {
    const $button = react_1.useRef(null);
    const $listbox = react_1.useRef(null);
    const [isOpen, _setIsOpen] = react_1.useState(initiallyOpen);
    const [focusedOptionIndex, setFocusedOptionIndex] = react_1.useState();
    const id = useUniqueId_1.useUniqueId();
    react_1.useEffect(() => {
        const node = $button.current;
        if (node) {
            if (focusedOptionIndex === null) {
                node.focus();
                return;
            }
            const activeChild = node.querySelector(`[id="${id}-${focusedOptionIndex}"]`);
            if (activeChild) {
                activeChild.focus();
            }
        }
    }, [focusedOptionIndex]);
    const getNextOptionIndex = react_1.useCallback(direction => {
        if (focusedOptionIndex === undefined || focusedOptionIndex === null) {
            return 0;
        }
        switch (direction) {
            case "down":
                if (focusedOptionIndex === itemCount - 1) {
                    return itemCount - 1;
                }
                if (focusedOptionIndex === null) {
                    return 0;
                }
                return focusedOptionIndex + 1;
            case "up":
                if (focusedOptionIndex === 0) {
                    return 0;
                }
                if (focusedOptionIndex === null) {
                    return 0;
                }
                return focusedOptionIndex - 1;
            default:
                return 0;
        }
    }, [focusedOptionIndex, itemCount]);
    const setIsOpen = react_1.useCallback((value) => {
        _setIsOpen(value);
        if (value === true && !focusedOptionIndex) {
            setFocusedOptionIndex(0);
        }
    }, [focusedOptionIndex]);
    const handleKeyDown = react_1.useCallback(e => {
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                }
                else if (!isMultiSelect) {
                    setIsOpen(false);
                }
                return;
            case "ArrowDown":
                e.preventDefault();
                e.stopPropagation();
                if (!isOpen) {
                    setIsOpen(true);
                    return;
                }
                setFocusedOptionIndex(getNextOptionIndex("down"));
                return;
            case "ArrowUp":
                e.preventDefault();
                e.stopPropagation();
                if (!isOpen) {
                    setIsOpen(true);
                    return;
                }
                setFocusedOptionIndex(getNextOptionIndex("up"));
                return;
            case "Home":
                e.preventDefault();
                e.stopPropagation();
                setFocusedOptionIndex(0);
                return;
            case "End":
                e.preventDefault();
                e.stopPropagation();
                setFocusedOptionIndex(itemCount - 1);
                return;
            case "Escape":
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                setFocusedOptionIndex(null);
                return;
            default:
                return;
        }
    }, [isOpen, focusedOptionIndex]);
    const handleButtonClick = react_1.useCallback(e => {
        const node = $button.current;
        if (e.target === node) {
            _setIsOpen(!isOpen);
        }
    }, [isOpen]);
    react_1.useEffect(() => {
        const node = $button.current;
        if (node) {
            node.addEventListener("keydown", handleKeyDown);
            node.addEventListener("click", handleButtonClick);
        }
        return () => {
            if (node) {
                node.removeEventListener("keydown", handleKeyDown);
                node.removeEventListener("click", handleButtonClick);
            }
        };
    }, [handleKeyDown]);
    if (itemCount < 1) {
        return {};
    }
    return {
        isOpen: isOpen,
        setIsOpen: setIsOpen,
        buttonProps: {
            ref: $button,
            tabIndex: isDisabled ? -1 : 0,
            role: "button",
            "aria-expanded": isOpen,
            "aria-haspopup": "listbox",
            "aria-disabled": Boolean(isDisabled),
        },
        listboxProps: Object.assign({ ref: $listbox, role: "listbox" }, (focusedOptionIndex !== null && {
            "aria-activedescendant": isOpen ? `${id}-${focusedOptionIndex}` : undefined,
        }), { style: !isOpen ? { display: "none" } : undefined }),
        getChildProps: (childItemIndex) => ({
            "aria-selected": focusedOptionIndex === childItemIndex ? true : undefined,
            role: "option",
            id: `${id}-${childItemIndex}`,
        }),
        focusedOptionIndex: focusedOptionIndex,
    };
};
//# sourceMappingURL=index.js.map